/* ============================================================================
   Slide 9 — "Nadi Shuddhi" — SHARED CONTENT (single source of truth)
   Read by the desktop layout (9_nadiShuddhi.tsx), the practice body
   (9_nadiShuddhi_body.tsx) and the mobile layout (HTLDSlide9Mobile).
   Edit copy ONLY here — English and German side by side; the German object
   must have exactly the same shape as the English one.
   ========================================================================== */
import { ByLang, useL } from "i18n/lang";

export const accent9 = "#3ecb8a";
export const mudraSrc9 = "/image/vishnuMudra.png";

export interface Seg { t: string; b?: boolean; em?: boolean; }

const en = {
  meta: {
    breadcrumbPre: "Breath Practice",
    breadcrumbAccent: "Anulom Vilom Pranayama",
    sanskrit: "अनुलोम विलोम प्राणायाम",
    headerTitlePre: "Nadi ",
    headerTitleAccent: "Shuddhi",
    titlePre: "Alternate Nostril ",
    titleAccentWord: "Breathing",
    titlePost: "",
    subtitle: "Nadi Shuddhi · Clears the energy channels, balances the brain",
    whatLabel: "What it does",
    cycleLabel: "The cycle · 4 · 2 · 6",
    durationLabel: "Duration & transition",
    mudraAlt: "Vishnu mudra — hand position for alternate-nostril breathing",
    /** one-line summary under the title of the portrait practice guide */
    guideSummary: "Alternate nostril · 4 · 2 · 6 — 5 rounds",
  },
  steps: [
    { step: "Close right nostril (thumb)",      desc: "Inhale through the LEFT for 4 counts." },
    { step: "Close both (thumb + ring finger)", desc: "Hold gently for 2 counts. No strain." },
    { step: "Release right, close left",        desc: "Exhale through the RIGHT for 6 counts." },
    { step: "Inhale through the RIGHT",         desc: "4 counts. Then hold 2. Then exhale LEFT for 6." },
    { step: "That is one full cycle",           desc: "Repeat for 5 rounds." },
  ] as { step: string; desc: string }[],
  what: [
    { t: "Nadi Shuddhi — meaning " },
    { t: "\"channel purification\"", b: true },
    { t: " — alternates the breath between left and right nostrils, balancing both brain hemispheres and clearing the Ida and Pingala nadis. It draws the mind from Alpha into the quieter " },
    { t: "Theta state.", b: true },
  ] as Seg[],
  duration: [
    { t: "Practise " },
    { t: "5 rounds", b: true },
    { t: " after Adham Pranayama. When the breath feels balanced, transition directly to the next step — Chandra Bhedana — left nostril only." },
  ] as Seg[],
  /** text drawn on the nostril-guide canvas (keys are the internal phase / nostril ids) */
  canvas: {
    phase: { INHALE: "INHALE", HOLD: "HOLD", EXHALE: "EXHALE" } as Record<string, string>,
    /** one-letter phase abbreviations for the phase bar (letter + count, e.g. I4) */
    abbr: { INHALE: "I", HOLD: "H", EXHALE: "E" } as Record<string, string>,
    left: "LEFT",
    right: "RIGHT",
    open: "OPEN",
    closed: "CLOSED",
    bothClosed: "BOTH CLOSED",
    nostril: { LEFT: "LEFT NOSTRIL", RIGHT: "RIGHT NOSTRIL" } as Record<string, string>,
    caption: "thumb → right · ring finger → left",
    pressToBegin: "press ▶ to begin",
    round: (n: number, total: number) => `round ${n} · of ${total}`,
  },
};

export type Slide9Content = typeof en;

const de: Slide9Content = {
  meta: {
    breadcrumbPre: "Atempraxis",
    breadcrumbAccent: "Anulom Vilom Pranayama",
    sanskrit: "अनुलोम विलोम प्राणायाम",
    headerTitlePre: "Nadi ",
    headerTitleAccent: "Shuddhi",
    titlePre: "Wechselatmung durch die ",
    titleAccentWord: "Nasenlöcher",
    titlePost: "",
    subtitle: "Nadi Shuddhi · Reinigt die Energiekanäle, gleicht das Gehirn aus",
    whatLabel: "Die Wirkung",
    cycleLabel: "Der Zyklus · 4 · 2 · 6",
    durationLabel: "Dauer & Übergang",
    mudraAlt: "Vishnu-Mudra – Handhaltung für die Wechselatmung",
    guideSummary: "Wechselatmung · 4 · 2 · 6 – 5 Runden",
  },
  steps: [
    { step: "Rechtes Nasenloch zu (Daumen)", desc: "LINKS 4 Zählzeiten einatmen." },
    { step: "Kurz beide zu (+ Ringfinger)",  desc: "Sanft 2 halten, ohne Druck." },
    { step: "Rechts öffnen, links bleibt zu", desc: "RECHTS 6 Zählzeiten ausatmen." },
    { step: "RECHTS einatmen",               desc: "4 Zählzeiten, 2 halten, LINKS 6 aus." },
    { step: "Ein voller Zyklus",             desc: "5 Runden wiederholen." },
  ],
  what: [
    { t: "Nadi Shuddhi – wörtlich " },
    { t: "„Reinigung der Kanäle“", b: true },
    { t: " – lässt den Atem zwischen linkem und rechtem Nasenloch wechseln, gleicht beide Gehirnhälften aus und reinigt die Nadis Ida und Pingala. So gleitet der Geist von Alpha in den stilleren " },
    { t: "Theta-Zustand.", b: true },
  ],
  duration: [
    { t: "Übe " },
    { t: "5 Runden", b: true },
    { t: " nach Adham Pranayama. Wenn sich der Atem ausgeglichen anfühlt, gehe direkt zum nächsten Schritt über – Chandra Bhedana – nur durch das linke Nasenloch." },
  ],
  canvas: {
    phase: { INHALE: "EINATMEN", HOLD: "HALTEN", EXHALE: "AUSATMEN" },
    abbr: { INHALE: "E", HOLD: "H", EXHALE: "A" },
    left: "LINKS",
    right: "RECHTS",
    open: "OFFEN",
    closed: "GESCHLOSSEN",
    bothClosed: "BEIDE GESCHLOSSEN",
    nostril: { LEFT: "LINKES NASENLOCH", RIGHT: "RECHTES NASENLOCH" },
    caption: "Daumen → rechts · Ringfinger → links",
    pressToBegin: "drücke ▶ zum Starten",
    round: (n: number, total: number) => `Runde ${n} · von ${total}`,
  },
};

export const slide9Content: ByLang<Slide9Content> = { en, de };
export const useSlide9 = () => useL(slide9Content);
